import React, { useState, useEffect } from 'react';
import { FlightInfo, FlightConnection } from './types';
import { useToast } from '@/hooks/use-toast';
import { useAmadeusApi } from '@/hooks/useAmadeusApi';
import { Button } from '@/components/ui/button';
import { Plus, Plane, Loader2, Search, Trash, Clock } from 'lucide-react';
import { FlightCollapsible } from './FlightCollapsible';
import { format } from 'date-fns';
import { 
  getDateFromISOString, 
  getTimeFromISOString, 
  calculateMinutesBetweenISOStrings,
  formatDuration,
  formatFlightNumber,
  validateFlightNumber,
  getAirlineName,
  parseISO,
  parseDurationToMinutes as parseDuration
} from '@/lib/quotation-utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { ImageManagerModal } from '@/components/ui/image-manager-modal';
import { cn } from '@/lib/utils';
import { FlightForm } from './FlightForm';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

interface FlightManagerProps {
  pageId: string;
  flights: FlightInfo[];
  updateFlights: (flights: FlightInfo[]) => void;
  collapsedItems: Record<string, boolean>;
  onToggleItem: (flightId: string) => void;
  hideAddButton?: boolean;
  hideTitle?: boolean;
  allPages?: any[]; // All pages in the quotation
  removeFlightFromPage?: (pageId: string, flightId: string) => void;
  addFlightToPage?: (pageId: string, flight: FlightInfo) => void;
  moveFlightToPage?: (sourcePageId: string, flightId: string, targetPageId: string) => void;
}

// Update the total flight time calculation function
export function calculateTotalFlightTime(flight: FlightInfo): string {
  if (!flight) return '0h 0min';
  
  let totalMinutes = 0;
  
  // If main flight has flightDuration, use it, otherwise calculate from timestamps
  if (flight.flightDuration) {
    totalMinutes = parseDuration(flight.flightDuration);
  } else if (flight.departureDateTime && flight.arrivalDateTime) {
    totalMinutes = calculateMinutesBetweenISOStrings(
      flight.departureDateTime,
      flight.arrivalDateTime
    );
  }
  
  // Add each connection's duration and waiting time
  if (flight.connections && flight.connections.length > 0) {
    flight.connections.forEach((connection, index) => {
      // Add connection flight duration
      if (connection.flightDuration) {
        totalMinutes += parseDuration(connection.flightDuration);
      } else if (connection.departureDateTime && connection.arrivalDateTime) {
        totalMinutes += calculateMinutesBetweenISOStrings(
          connection.departureDateTime,
          connection.arrivalDateTime
        );
      }
      
      // Add waiting time before this connection
      if (connection.waitingTime) {
        // Use explicit waiting time if available
        totalMinutes += parseDuration(connection.waitingTime);
      } else if (index === 0) {
        // Waiting time between main flight and first connection
        if (flight.arrivalDateTime && connection.departureDateTime) {
          const waitingTime = calculateMinutesBetweenISOStrings(
            flight.arrivalDateTime,
            connection.departureDateTime
          );
          totalMinutes += waitingTime;
        }
      } else if (index > 0 && flight.connections[index - 1].arrivalDateTime && connection.departureDateTime) {
        // Waiting time between consecutive connections
        const waitingTime = calculateMinutesBetweenISOStrings(
          flight.connections[index - 1].arrivalDateTime,
          connection.departureDateTime
        );
        totalMinutes += waitingTime;
      }
    });
  }
  
  // Format using hours and minutes
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  
  return `${hours}h ${minutes}min`;
}

// Add the connection waiting time calculation function
export function calculateConnectionDuration(mainFlight: FlightInfo, connection: FlightConnection, connectionIndex: number): string {
  // If there's an explicitly set waiting time, use it
  if (connection.waitingTime) {
    return connection.waitingTime;
  }

  try {
    // For the first connection, calculate between main flight arrival and connection departure
    if (connectionIndex === 0) {
      if (!mainFlight.arrivalDateTime || !connection.departureDateTime) {
        return 'Não disponível';
      }

      const arrivalTime = new Date(mainFlight.arrivalDateTime);
      const departureTime = new Date(connection.departureDateTime);
      
      const waitTimeMs = departureTime.getTime() - arrivalTime.getTime();
      
      if (waitTimeMs < 0) return 'Não disponível';
      
      const waitTimeMinutes = Math.round(waitTimeMs / (1000 * 60));
      const hours = Math.floor(waitTimeMinutes / 60);
      const minutes = waitTimeMinutes % 60;
      
      return `${hours}h ${minutes}m`;
    } 
    // For subsequent connections, calculate between previous connection arrival and this connection departure
    else {
      const previousConnection = mainFlight.connections?.[connectionIndex - 1];
      if (!previousConnection?.arrivalDateTime || !connection.departureDateTime) {
        return 'Não disponível';
      }

      const arrivalTime = new Date(previousConnection.arrivalDateTime);
      const departureTime = new Date(connection.departureDateTime);
      
      const waitTimeMs = departureTime.getTime() - arrivalTime.getTime();
      
      if (waitTimeMs < 0) return 'Não disponível';
      
      const waitTimeMinutes = Math.round(waitTimeMs / (1000 * 60));
      const hours = Math.floor(waitTimeMinutes / 60);
      const minutes = waitTimeMinutes % 60;
      
      return `${hours}h ${minutes}m`;
    }
  } catch (e) {
    return 'Não disponível';
  }
}

export function FlightManager({ 
  pageId, 
  flights, 
  updateFlights,
  collapsedItems,
  onToggleItem,
  hideAddButton = false, 
  hideTitle = false, 
  allPages = [], 
  removeFlightFromPage, 
  addFlightToPage,
  moveFlightToPage
}: FlightManagerProps) {
  const { toast } = useToast();
  const { searchFlightByNumber, getAirlineInfo } = useAmadeusApi();
  const [isFlightImageManagerOpen, setIsFlightImageManagerOpen] = useState(false);
  const [currentFlightId, setCurrentFlightId] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSearchingConnection, setIsSearchingConnection] = useState(false);
  const [collapsedFlights, setCollapsedFlights] = useState<Record<string, boolean>>({});
  const [currentFlightIndex, setCurrentFlightIndex] = useState<number | null>(null);
  const [currentConnectionIndex, setCurrentConnectionIndex] = useState<number | null>(null);

  // Initialize collapse states for all existing flights
  useEffect(() => {
    const initialState: Record<string, boolean> = {};
    flights.forEach(flight => {
      initialState[flight.id] = collapsedItems[flight.id] !== false; // Default to collapsed
    });
    setCollapsedFlights(initialState);
  }, []);

  // Listen for expansion events to collapse flights when another item is expanded
  useEffect(() => {
    const handleItemExpanded = (event: CustomEvent) => {
      const { itemType, itemId, pageId: eventPageId } = event.detail;
      
      // If the event is from another page or for a flight, ignore
      if (eventPageId !== pageId || itemType === 'flight') {
        return;
      }
      
      // Collapse all flights when another type of item is expanded
      const allCollapsed = Object.keys(collapsedFlights).reduce((acc, id) => {
        acc[id] = true;
        return acc;
      }, {} as Record<string, boolean>);
      
      setCollapsedFlights(allCollapsed);
    };
    
    // Add the event listener
    document.addEventListener('item-expanded', handleItemExpanded as EventListener);
    
    // Remove when component unmounts
    return () => {
      document.removeEventListener('item-expanded', handleItemExpanded as EventListener);
    };
  }, [pageId, collapsedFlights]);

  // Function to add a new flight
  const addFlight = () => {
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    const newFlight: FlightInfo = {
      id: `flight-${Date.now()}`,
      airline: '',
      flightNumber: '',
      departureAirport: '',
      departureDateTime: `${formattedDate}T00:00:00`,
      arrivalAirport: '',
      arrivalDateTime: `${formattedDate}T02:00:00`,
      flightDuration: '',
      connections: [],
      images: [],
      globalOrder: flights.length + 1 // Increment by 1 to place at the end
    };
    
    const updatedFlights = [...(flights || []), newFlight];
    updateFlights(updatedFlights);
    
    // Expand the new flight after a small delay to ensure it's in the DOM
    setTimeout(() => {
      setCollapsedFlights(prev => ({
        ...prev,
        [newFlight.id]: false
      }));
      
      // Dispatch event about the expansion
      const event = new CustomEvent('item-expanded', {
        detail: {
          itemType: 'flight',
          itemId: newFlight.id,
          pageId
        }
      });
      document.dispatchEvent(event);
    }, 0);
  };

  // Function to toggle the collapse state of a flight
  const toggleFlightCollapse = (flightId: string) => {
    // Update the collapsed state for this flight
    setCollapsedFlights(prev => {
      const newState = {
        ...prev,
        [flightId]: !prev[flightId]
      };
      
      // If expanding, dispatch event
      if (!newState[flightId]) {
        const event = new CustomEvent('item-expanded', {
          detail: {
            itemType: 'flight',
            itemId: flightId,
            pageId
          }
        });
        document.dispatchEvent(event);
      }
      
      return newState;
    });
  };

  // Function to remove a flight
  const removeFlight = (flightId: string) => {
    const updatedFlights = flights.filter(flight => flight.id !== flightId);
    updateFlights(updatedFlights);
  };

  // Function to update a flight field
  const updateFlight = (pageId: string, flightId: string, field: keyof FlightInfo, value: any) => {
    const updatedFlights = flights.map(flight => {
      if (flight.id === flightId) {
        return { ...flight, [field]: value };
      }
      return flight;
    });
    
    updateFlights(updatedFlights);
  };

  // Function to update multiple flight fields at once
  const batchUpdateFlight = (flightId: string, updates: Partial<FlightInfo>) => {
    console.log('[FlightManager] Batch updating flight:', {
      flightId,
      updates
    });
    
    const updatedFlights = flights.map((flight: FlightInfo) => {
      if (flight.id === flightId) {
        // Create a deep copy of the flight to avoid reference issues
        const updated = JSON.parse(JSON.stringify({
          ...flight,
          ...updates
        }));
        
        // Ensure departure coordinates are handled properly if provided
        if (updates.departureCoordinates) {
          updated.departureCoordinates = {
            lat: Number(updates.departureCoordinates.lat),
            lng: Number(updates.departureCoordinates.lng)
          };
        }
        
        // Ensure arrival coordinates are handled properly if provided
        if (updates.arrivalCoordinates) {
          updated.arrivalCoordinates = {
            lat: Number(updates.arrivalCoordinates.lat),
            lng: Number(updates.arrivalCoordinates.lng)
          };
        }
        
        console.log('[FlightManager] Updated flight in batch:', {
          flightId,
          airline: flight.airline,
          flightNumber: flight.flightNumber,
          updatedKeys: Object.keys(updates),
          departureCoordinates: {
            before: flight.departureCoordinates,
            after: updated.departureCoordinates
          },
          arrivalCoordinates: {
            before: flight.arrivalCoordinates,
            after: updated.arrivalCoordinates
          }
        });
        
        return updated;
      }
      return flight;
    });
    
    updateFlights(updatedFlights);
  };

  // Function to add a connection to a flight
  const addFlightConnection = (pageId: string, flightId: string) => {
    const flight = flights.find(f => f.id === flightId);
    if (!flight) return;
    
    const currentDate = new Date();
    const formattedDate = format(currentDate, "yyyy-MM-dd");
    
    // Create a new connection with default values
    const newConnection: FlightConnection = {
      id: `connection-${Date.now()}`,
      airline: '',
      flightNumber: '',
      departureAirport: flight.arrivalAirport,
      departureDateTime: flight.arrivalDateTime || `${formattedDate}T00:00:00`,
      arrivalAirport: '',
      arrivalDateTime: flight.arrivalDateTime ? new Date(new Date(flight.arrivalDateTime).getTime() + 2 * 60 * 60 * 1000).toISOString() : `${formattedDate}T02:00:00`,
      flightDuration: '',
      notes: ''
    };
    
    // Add the connection to the flight
    const updatedFlights = flights.map(f => {
      if (f.id === flightId) {
        return {
          ...f,
          connections: [...(f.connections || []), newConnection]
        };
      }
      return f;
    });
    
    updateFlights(updatedFlights);
  };

  // Function to update a connection field
  const updateFlightConnection = (pageId: string, flightId: string, connectionId: string, field: string, value: any) => {
    // Find the flight with the given ID
    const flight = flights.find(f => f.id === flightId);
    if (!flight) return;
    
    // Create updated connections array
    const updatedConnections = flight.connections.map(connection => {
      if (connection.id === connectionId) {
        return { ...connection, [field]: value };
      }
      return connection;
    });
    
    // Update the flight with the updated connections
    const updatedFlights = flights.map(f => {
      if (f.id === flightId) {
        return { ...f, connections: updatedConnections };
      }
      return f;
    });
    
    updateFlights(updatedFlights);
  };

  // Function to remove a connection
  const removeFlightConnection = (pageId: string, flightId: string, connectionId: string) => {
    // Find the flight with the given ID
    const flight = flights.find(f => f.id === flightId);
    if (!flight) return;
    
    // Filter out the connection to remove
    const updatedConnections = flight.connections.filter(connection => connection.id !== connectionId);
    
    // Update the flight with the updated connections
    const updatedFlights = flights.map(f => {
      if (f.id === flightId) {
        return { ...f, connections: updatedConnections };
      }
      return f;
    });
    
    updateFlights(updatedFlights);
  };

  // Function to handle drag and drop reordering
  const handleDragEnd = (result: any) => {
    // Dropped outside the list
    if (!result.destination) {
      return;
    }
    
    // Reorder the list
    const items = Array.from(flights);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update the globalOrder field for each flight
    const updatedItems = items.map((item, index) => ({
      ...item,
      globalOrder: index
    }));
    
    updateFlights(updatedItems);
  };

  // Function to open the image manager for a flight
  const openFlightImageManager = (flightId: string) => {
    setCurrentFlightId(flightId);
    setIsFlightImageManagerOpen(true);
  };

  // Function to search for flight information
  const searchFlight = async (flightId: string) => {
    setIsSearching(true);
    setCurrentFlightId(flightId);
    
    // Find the flight index to update loading state
    const flightIndex = flights.findIndex(f => f.id === flightId);
    setCurrentFlightIndex(flightIndex);
    
    const flight = flights.find(f => f.id === flightId);
    if (!flight || !flight.flightNumber || !flight.departureDateTime) {
      toast({
        title: "Informações insuficientes",
        description: "Por favor, preencha o número do voo e a data de partida.",
        variant: "destructive"
      });
      setIsSearching(false);
      setCurrentFlightIndex(null);
      return;
    }
    
    try {
      // Formatar o número do voo para a API (remover espaços e separar letra e número)
      const formattedFlightNumber = formatFlightNumber(flight.flightNumber);
      
      // Extrair a data do ISO string para o formato YYYY-MM-DD
      const searchDate = flight.departureDateTime.split('T')[0];
      
      toast({
        title: "Buscando voo",
        description: `Buscando informações para o voo ${formattedFlightNumber} em ${searchDate}`,
      });
      
      // Usar a API real em vez dos dados simulados
      const results = await searchFlightByNumber(formattedFlightNumber, searchDate);
      
      if (results && results.length > 0) {
        // Pegar o primeiro resultado
        const flightData = results[0];
        const firstItinerary = flightData.itineraries[0];
        const firstSegment = firstItinerary.segments[0];
        const lastSegment = firstItinerary.segments[firstItinerary.segments.length - 1];
        
        // Create an updated flight object with all the fields at once
        const updatedFlight = {
          ...flight,
          departureAirport: firstSegment.departure.iataCode,
          departureTerminal: firstSegment.departure.terminal || flight.departureTerminal,
          departureDateTime: firstSegment.departure.at,
          arrivalAirport: lastSegment.arrival.iataCode,
          arrivalTerminal: lastSegment.arrival.terminal || flight.arrivalTerminal,
          arrivalDateTime: lastSegment.arrival.at,
          flightDuration: formatDuration(
            calculateMinutesBetweenISOStrings(
              firstSegment.departure.at,
              lastSegment.arrival.at
            )
          )
        };
        
        // Buscar informações da companhia aérea com base no código do voo
        const carrierCode = firstSegment.carrierCode;
        
        try {
          const airlineInfo = await getAirlineInfo(carrierCode);
          if (airlineInfo) {
            updatedFlight.airline = airlineInfo.businessName;
          } else {
            // Fallback para o nome da companhia aérea se a API não retornar
            updatedFlight.airline = getAirlineName(carrierCode);
          }
        } catch (error) {
          // Se falhar, usar o nome do getAirlineName
          updatedFlight.airline = getAirlineName(carrierCode);
        }
        
        // Update all flights at once with the updated flight
        const updatedFlights = flights.map(f => 
          f.id === flightId ? updatedFlight : f
        );
        
        // Update the flights state with all changes at once
        updateFlights(updatedFlights);
        
        toast({
          title: "Voo encontrado",
          description: `Informações do voo ${flight.flightNumber} foram atualizadas.`,
        });
      } else {
        toast({
          title: "Voo não encontrado",
          description: "Não foi possível encontrar informações para este voo na data especificada.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Erro na busca do voo:", error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar informações do voo.",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
      setCurrentFlightIndex(null);
    }
  };

  // Function to search for connection flight information
  const searchConnectionFlight = async (flightId: string, connectionId: string) => {
    setIsSearchingConnection(true);
    
    // Find flight and connection indices to update loading state
    const flightIndex = flights.findIndex(f => f.id === flightId);
    setCurrentFlightIndex(flightIndex);
    
    const flight = flights.find(f => f.id === flightId);
    if (!flight || !flight.connections) {
      setIsSearchingConnection(false);
      setCurrentFlightIndex(null);
      setCurrentConnectionIndex(null);
      return;
    }
    
    const connectionIndex = flight.connections.findIndex(c => c.id === connectionId);
    setCurrentConnectionIndex(connectionIndex);
    
    const connection = flight.connections.find(c => c.id === connectionId);
    console.log("Searching connection flight:", { flightId, connectionId, connection });
    
    if (!connection || !connection.flightNumber || !connection.departureDateTime) {
      toast({
        title: "Informações insuficientes",
        description: "Por favor, preencha o número do voo e a data de partida da conexão.",
        variant: "destructive"
      });
      setIsSearchingConnection(false);
      setCurrentFlightIndex(null);
      setCurrentConnectionIndex(null);
      return;
    }
    
    try {
      // Formatar o número do voo para a API
      const formattedFlightNumber = formatFlightNumber(connection.flightNumber);
      
      // Extrair a data do ISO string para o formato YYYY-MM-DD
      const searchDate = connection.departureDateTime.split('T')[0];
      
      toast({
        title: "Buscando voo de conexão",
        description: `Buscando informações para o voo ${formattedFlightNumber} em ${searchDate}`,
      });
      
      // Usar a API real 
      const results = await searchFlightByNumber(formattedFlightNumber, searchDate);
      console.log("Connection flight search results:", results);
      
      if (results && results.length > 0) {
        // Pegar o primeiro resultado
        const flightData = results[0];
        const firstItinerary = flightData.itineraries[0];
        const firstSegment = firstItinerary.segments[0];
        const lastSegment = firstItinerary.segments[firstItinerary.segments.length - 1];
        
        console.log("Connection segments:", { firstSegment, lastSegment });
        
        // Create a new connection object with all the updated fields at once
        // This ensures that all fields are updated together rather than one by one
        const updatedConnection = {
          ...connection,
          departureAirport: firstSegment.departure.iataCode,
          departureTerminal: firstSegment.departure.terminal || connection.departureTerminal,
          departureDateTime: firstSegment.departure.at,
          arrivalAirport: lastSegment.arrival.iataCode,
          arrivalTerminal: lastSegment.arrival.terminal || connection.arrivalTerminal,
          arrivalDateTime: lastSegment.arrival.at,
          flightDuration: formatDuration(
            calculateMinutesBetweenISOStrings(
              firstSegment.departure.at, 
              lastSegment.arrival.at
            )
          )
        };
        
        // Buscar informações da companhia aérea com base no código do voo
        const carrierCode = firstSegment.carrierCode;
        console.log("Connection airline info:", { carrierCode });
        
        try {
          const airlineInfo = await getAirlineInfo(carrierCode);
          console.log("Airline API response:", airlineInfo);
          
          // Add airline info to the updated connection
          if (airlineInfo) {
            updatedConnection.airline = airlineInfo.businessName;
          } else {
            // Fallback para o nome da companhia aérea se a API não retornar
            updatedConnection.airline = getAirlineName(carrierCode);
          }
        } catch (error) {
          console.error("Error getting airline info:", error);
          // Se falhar, usar o nome do getAirlineName
          updatedConnection.airline = getAirlineName(carrierCode);
        }
        
        // Update the entire connections array at once
        const updatedConnections = flight.connections.map(c => 
          c.id === connectionId ? updatedConnection : c
        );
        
        // Update the flight with all the updated connections
        const updatedFlights = flights.map(f => 
          f.id === flightId 
            ? { ...f, connections: updatedConnections } 
            : f
        );
        
        // Update the flights state with all changes at once
        updateFlights(updatedFlights);
        
        console.log("Connection after updates:", updatedConnection);
        
        toast({
          title: "Voo de conexão encontrado",
          description: `Informações do voo ${connection.flightNumber} foram atualizadas.`,
        });
      } else {
        toast({
          title: "Voo de conexão não encontrado",
          description: "Não foi possível encontrar informações para este voo na data especificada.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Erro na busca do voo de conexão:", error);
      toast({
        title: "Erro na busca",
        description: "Ocorreu um erro ao buscar informações do voo de conexão.",
        variant: "destructive"
      });
    } finally {
      setIsSearchingConnection(false);
      setCurrentFlightIndex(null);
      setCurrentConnectionIndex(null);
    }
  };

  // Function to duplicate a flight
  const duplicateFlight = (sourcePageId: string, flightId: string) => {
    const flightToDuplicate = flights.find(
      (flight: FlightInfo) => flight.id === flightId
    );
    
    if (!flightToDuplicate) return;
    
    // Deep clone the flight object
    const duplicatedFlight: FlightInfo = JSON.parse(JSON.stringify(flightToDuplicate));
    
    // Update properties for the duplicate
    duplicatedFlight.id = `flight-${Date.now()}`;
    duplicatedFlight.airline = duplicatedFlight.airline || '';
    duplicatedFlight.flightNumber = duplicatedFlight.flightNumber ? `${duplicatedFlight.flightNumber} (Cópia)` : '';
    duplicatedFlight.globalOrder = flights.length + 1; // Place at the end
    
    // Add the duplicated flight to the list
    updateFlights([...flights, duplicatedFlight]);
    
    // Notify parent component that a new item was added and should be expanded
    setTimeout(() => {
      setCollapsedFlights(prev => ({
        ...prev,
        [duplicatedFlight.id]: false
      }));
      
      // Dispatch event about the expansion
      const event = new CustomEvent('item-expanded', {
        detail: {
          itemType: 'flight',
          itemId: duplicatedFlight.id,
          pageId
        }
      });
      document.dispatchEvent(event);
    }, 0);
    
    toast({
      title: "Voo duplicado",
      description: "O voo foi duplicado com sucesso.",
    });
  };

  return (
    <div className="space-y-4">
      {!hideTitle && (
        <div className="flex justify-between items-center">
          <h3 className="text-base font-medium">Voos</h3>
          {!hideAddButton && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={addFlight}
              className="bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Voo
            </Button>
          )}
        </div>
      )}
      
      {hideTitle && !hideAddButton && (
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            size="sm"
            onClick={addFlight}
            className="bg-white text-blue-500 border-blue-500 hover:bg-blue-500 hover:text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar Voo
          </Button>
        </div>
      )}
      
      {flights && flights.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="flights">
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="space-y-3"
              >
                {flights
                  .sort((a, b) => (a.globalOrder || 0) - (b.globalOrder || 0))
                  .map((flight, index) => (
                    <Draggable key={flight.id} draggableId={flight.id} index={index}>
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className="mb-3"
                        >
                          <FlightForm
                            flight={flight}
                            index={index}
                            pageId={pageId}
                            updateFlight={updateFlight}
                            batchUpdateFlight={batchUpdateFlight}
                            updateFlightConnection={updateFlightConnection}
                            addFlightConnection={addFlightConnection}
                            removeFlightConnection={removeFlightConnection}
                            deleteFlight={removeFlight}
                            duplicateFlight={duplicateFlight}
                            moveFlightToPage={moveFlightToPage}
                            isCollapsed={!!collapsedFlights[flight.id]}
                            onToggleCollapse={() => toggleFlightCollapse(flight.id)}
                            searchFlight={searchFlight}
                            searchConnectionFlight={searchConnectionFlight}
                            allPages={allPages}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <div className="text-center py-6 border border-dashed rounded-md">
          <Plane className="h-8 w-8 mx-auto text-gray-400 mb-2" />
          <p className="text-gray-500">Nenhum voo adicionado.</p>
          <p className="text-sm text-gray-400">Clique no botão acima para adicionar um voo.</p>
        </div>
      )}

      {isFlightImageManagerOpen && currentFlightId && (
        <ImageManagerModal
          isOpen={isFlightImageManagerOpen}
          onClose={() => setIsFlightImageManagerOpen(false)}
          images={flights.find(f => f.id === currentFlightId)?.images || []}
          onImagesChange={(images) => {
            updateFlight(pageId, currentFlightId, 'images', images);
            setIsFlightImageManagerOpen(false);
          }}
          contextId={currentFlightId}
          contextType="flight"
        />
      )}
    </div>
  );
} 